import { useState, useEffect, useRef } from 'react';
import ModalBase from '@/components/base/ModalBase';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { RadioEvent, EventType, EventStatus, EventModality, EventCategory } from '@/mocks/adminEvents';
import {
  eventTypeLabels, eventTypeIcons, eventTypeColors,
  eventStatusLabels,
  eventModalityLabels, eventModalityIcons, eventModalityColors,
  eventCategoryLabels, eventCategoryIcons, eventCategoryColors,
} from '@/mocks/adminEvents';
import AdminImageUpload from '@/components/base/AdminImageUpload';

interface EventFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (event: Partial<RadioEvent>) => void;
  event?: RadioEvent | null;
}

const types: EventType[] = ['concierto', 'meet_greet', 'transmision_especial', 'firma_autografos', 'sesion_acustica', 'lanzamiento'];
const statuses: EventStatus[] = ['borrador', 'publicado', 'finalizado', 'cancelado'];
const modalities: EventModality[] = ['presencial', 'online', 'hibrido'];
const categories: EventCategory[] = ['musica', 'deportes', 'comunidad', 'cultural', 'religioso', 'familiar', 'educativo'];

export default function EventFormModal({ open, onClose, onSave, event }: EventFormModalProps) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    type: 'concierto' as EventType,
    status: 'borrador' as EventStatus,
    modality: 'presencial' as EventModality,
    capacity: '',
    category: 'musica' as EventCategory,
    image: '',
  });

  const [saving, setSaving] = useState(false);
  const imageBlobRef = useRef<string | null>(null);

  useEffect(() => {
    if (event) {
      setForm({
        title: event.title,
        description: event.description,
        date: event.date,
        time: event.time,
        location: event.location,
        type: event.type,
        status: event.status,
        modality: event.modality,
        capacity: String(event.capacity || ''),
        category: event.category,
        image: event.image,
      });
    } else {
      const today = new Date().toISOString().slice(0, 10);
      setForm({
        title: '',
        description: '',
        date: today,
        time: '18:00',
        location: '',
        type: 'concierto',
        status: 'borrador',
        modality: 'presencial',
        capacity: '',
        category: 'musica',
        image: '',
      });
    }
  }, [event, open]);

  const titleOk = form.title.trim().length > 0;
  const locationOk = form.location.trim().length > 0;
  const capacityNum = parseInt(form.capacity, 10);
  const isNew = !event;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!titleOk) {
      await SwalAlert.warning('Validación', 'El título del evento es requerido.');
      return;
    }
    if (!locationOk) {
      await SwalAlert.warning('Validación', 'La ubicación del evento es requerida.');
      return;
    }

    setSaving(true);
    onSave({
      ...form,
      capacity: isNaN(capacityNum) ? 0 : capacityNum,
      id: event?.id || Date.now(),
      stationId: event?.stationId || 1,
      participants: event?.participants || 0,
      interested: event?.interested || 0,
      shares: event?.shares || 0,
      followers: event?.followers || 0,
    });
    onClose();
    setSaving(false);
    await SwalAlert.toast(event ? 'Evento actualizado.' : 'Evento creado.', 'success');
  };

  const mapsQuery = form.location ? encodeURIComponent(form.location) : '';

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={isNew ? 'Nuevo evento' : 'Editar evento'}
      subtitle="Completá los datos del evento"
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 font-semibold text-sm cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="event-form"
            disabled={saving}
            className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm cursor-pointer transition-all whitespace-nowrap ${
              titleOk && locationOk
                ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                : 'bg-primary-200 text-primary-400 cursor-not-allowed'
            }`}
          >
            {saving ? 'Guardando…' : isNew ? 'Crear evento' : 'Guardar cambios'}
          </button>
        </>
      }
    >
      <form id="event-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">
            Título del evento <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className={`w-full px-3 py-2 rounded-lg bg-background-100 border text-foreground-800 text-sm outline-none transition-colors ${
              !titleOk && form.title !== '' ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-primary-300'
            }`}
            placeholder="Ej: Summer Fest 2026"
          />
        </div>

        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Descripción</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors resize-none"
            rows={3}
            placeholder="Describí el evento, qué incluye, cómo participar..."
            maxLength={500}
          ></textarea>
          <p className="text-foreground-300 text-[10px] mt-1 text-right">{form.description.length}/500</p>
        </div>

        <div>
          <AdminImageUpload
            label="Imagen del evento"
            currentImage={form.image}
            onChange={(file) => {
              if (imageBlobRef.current) URL.revokeObjectURL(imageBlobRef.current);
              const blobUrl = URL.createObjectURL(file);
              imageBlobRef.current = blobUrl;
              setForm({ ...form, image: blobUrl });
              SwalAlert.toast('Imagen cargada correctamente.', 'success');
            }}
            onRemove={() => {
              if (imageBlobRef.current) URL.revokeObjectURL(imageBlobRef.current);
              imageBlobRef.current = null;
              setForm({ ...form, image: '' });
            }}
            recommendedDimensions="1200×630px"
            previewHeight="h-44"
            placeholderIcon="ri-image-add-line"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Fecha</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors"
            />
          </div>
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Hora</label>
            <input
              type="time"
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors"
            />
          </div>
        </div>

        {/* Modalidad — primary focus */}
        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Modalidad</label>
          <div className="grid grid-cols-3 gap-2">
            {modalities.map((m) => {
              const mc = eventModalityColors[m];
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm({ ...form, modality: m })}
                  className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl text-[10px] font-semibold cursor-pointer transition-all border ${
                    form.modality === m
                      ? 'ring-2 ring-offset-1 ring-primary-300 shadow-sm ' + mc.bg + ' ' + mc.text + ' ' + mc.border
                      : mc.bg + ' ' + mc.text + ' ' + mc.border + ' opacity-60 hover:opacity-90'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${mc.dot}`}></span>
                  <i className={`${eventModalityIcons[m]} text-base`}></i>
                  {eventModalityLabels[m]}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Cupo</label>
            <input
              type="number"
              min="0"
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors"
              placeholder="0 = Sin límite"
            />
            <p className="text-foreground-300 text-[10px] mt-1">Dejá en 0 si no tiene cupo limitado</p>
          </div>

          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">
              Ubicación <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg bg-background-100 border text-foreground-800 text-sm outline-none transition-colors ${
                !locationOk && form.location !== '' ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-primary-300'
              }`}
              placeholder="Ej: Cabina Principal - Av. Siempre Viva 742"
            />
          </div>
        </div>

        {form.location && form.modality !== 'online' && (
          <div className="rounded-lg overflow-hidden border border-background-200 h-[140px]">
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zEkVBtLg3zCOOgHvEFkIFcDU&q=${mapsQuery}&language=es&zoom=14`}
              title="Vista previa del mapa"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            ></iframe>
          </div>
        )}

        {/* Category */}
        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Categoría</label>
          <div className="flex items-center gap-1.5 flex-wrap">
            {categories.map((c) => {
              const cc = eventCategoryColors[c];
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, category: c })}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold cursor-pointer transition-all whitespace-nowrap border ${
                    form.category === c
                      ? cc.bg + ' ' + cc.text + ' ' + cc.border + ' ring-1 ring-primary-300'
                      : cc.bg + ' ' + cc.text + ' ' + cc.border + ' opacity-60 hover:opacity-90'
                  }`}
                >
                  <i className={`${eventCategoryIcons[c]} text-[10px]`}></i>
                  {eventCategoryLabels[c]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tipo de evento */}
        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Tipo de evento</label>
          <div className="grid grid-cols-3 gap-1.5">
            {types.map((t) => {
              const colors = eventTypeColors[t];
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, type: t })}
                  className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-[10px] font-semibold cursor-pointer transition-all ${
                    form.type === t
                      ? 'ring-2 ring-offset-1 ring-primary-300 ' + colors.bg + ' ' + colors.text + ' border ' + colors.border
                      : colors.bg + ' ' + colors.text + ' border ' + colors.border + ' opacity-60 hover:opacity-90'
                  }`}
                >
                  <i className={`${eventTypeIcons[t]} text-sm`}></i>
                  {eventTypeLabels[t]}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Estado</label>
          <div className="flex items-center gap-2 flex-wrap">
            {statuses.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setForm({ ...form, status: s })}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  form.status === s
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}
              >
                {eventStatusLabels[s]}
              </button>
            ))}
          </div>
        </div>
      </form>
    </ModalBase>
  );
}